import { useState } from 'react'
import { BookOpen, Check, Copy, RefreshCw, ThumbsUp } from 'lucide-react'
import { conversationCache, useSendFeedback } from '@/api/queries'
import type { FeedbackReason, MessageFeedback } from '@/api/types'
import { stripCitations } from '@/lib/citations'
import { cn } from '@/lib/cn'
import { queryClient } from '@/lib/queryClient'
import { useCopy } from '@/hooks/useCopy'
import { chatController } from '@/stores/chatController'
import { IconButton } from '@/components/ui/IconButton'
import { FeedbackPopover } from './FeedbackPopover'

type Props = {
  content: string
  /** Server message id; null/local ids can't receive feedback yet. */
  messageId: string | null
  conversationId: string | null
  feedback: MessageFeedback | null | undefined
  sourceCount: number
  canRegenerate: boolean
  onRegenerate: () => void
  onShowSources: () => void
}

export function MessageActions({
  content,
  messageId,
  conversationId,
  feedback,
  sourceCount,
  canRegenerate,
  onRegenerate,
  onShowSources,
}: Props) {
  const { copied, copy } = useCopy()
  const sendFeedback = useSendFeedback()
  /** Optimistic override; undefined = use the stored feedback. */
  const [optimistic, setOptimistic] = useState<MessageFeedback | null | undefined>(undefined)
  const current = optimistic !== undefined ? optimistic : feedback
  const rating = current?.rating

  const submit = async (value: MessageFeedback & { comment?: string }): Promise<boolean> => {
    if (!messageId) return false
    const next: MessageFeedback = { rating: value.rating, reason: value.reason ?? null }
    setOptimistic(next)
    try {
      await sendFeedback.mutateAsync({
        messageId,
        body: {
          rating: value.rating,
          ...(value.reason ? { reason: value.reason as FeedbackReason } : {}),
          ...(value.comment ? { comment: value.comment } : {}),
        },
      })
      if (conversationId) conversationCache.patchMessage(queryClient, conversationId, messageId, { feedback: next })
      chatController.setLiveFeedback(messageId, next)
      return true
    } catch {
      setOptimistic(undefined)
      return false
    }
  }

  return (
    <div className="mt-3 flex flex-wrap items-center gap-0.5 transition-opacity duration-300">
      <IconButton
        label={copied ? 'Copied' : 'Copy answer'}
        size="xs"
        onClick={() => void copy(stripCitations(content))}
      >
        {copied ? <Check /> : <Copy />}
      </IconButton>
      {canRegenerate && (
        <IconButton label="Regenerate answer" size="xs" onClick={onRegenerate}>
          <RefreshCw />
        </IconButton>
      )}
      {messageId && (
        <>
          <IconButton
            label="Good answer"
            size="xs"
            aria-pressed={rating === 1}
            active={rating === 1}
            onClick={() => void submit({ rating: 1 })}
          >
            <ThumbsUp className={cn(rating === 1 && 'fill-current')} />
          </IconButton>
          <FeedbackPopover
            active={rating === -1}
            onSubmit={(reason, comment) => submit({ rating: -1, reason, comment })}
          />
        </>
      )}
      {sourceCount > 0 && (
        <button
          type="button"
          onClick={onShowSources}
          className="pressable ml-2 inline-flex h-7 items-center gap-1.5 rounded-full bg-surface px-3 text-xs font-medium text-ink-muted shadow-card hover:text-ink hover:shadow-lift"
        >
          <BookOpen className="size-3.5" aria-hidden />
          {sourceCount} {sourceCount === 1 ? 'source' : 'sources'}
        </button>
      )}
    </div>
  )
}
